// REQ-078 / REQ-082 — Tab "Brillamos Juntos": ranking de nominaciones de la campaña + KPIs en vivo
// + export Excel multi-hoja y export de ganadores por cascadeo (raw|cascaded, CA-046 / WP-08).
// SIN puntos. Respeta la supresión por anonimato.
//
// T-10 (UAT, 18-ago-2026) — el tab es POR CAMPAÑA y necesita su SELECTOR. Sin él llamaba al backend
// con `campaignId` vacío: recibía el agregado del SCOPE bajo el nombre literal «Brillamos», tres de
// los cinco KPIs pintaban «—» para siempre y el «75» de «personas reconocidas» era el conteo del
// scope y de todos los orígenes. Con campaña elegida, los cinco KPIs salen del resumen de ESA
// campaña y el ranking sale de sus recibos.
//
// El LIBRO EXCEL (las 7 hojas de `02 - Campana brillamos juntos.xlsx`) se arma en
// `brillamosExcel.ts`: ahí viven la forma visual calcada de la plantilla del cliente y los textos
// en los 7 idiomas. Este archivo se queda con lo que sí es suyo — resolver los datos de la campaña
// y disparar la descarga. Antes ambas cosas convivían aquí, y la especificación de estilos ocupaba
// más de la mitad del archivo.
//
// «Todos los reconocimientos» consume el export CRUDO paginado (`winnersApi.exportNominationsRaw`,
// hasta 5000 filas, ver `fetchNominacionesCrudasPaginado` en `../../fetchNominacionesCrudas.ts`,
// compartida con `AdminWinners.tsx`), no el Top 50/200 de `ShineRankEntryDto`.
//
// «Distribución Fundamental» y «Embudo Adopción» quedaron corregidos en el backend
// (`GetRecognitionCampaignQueryHandler`) tras el bug de producción del 26-ago-2026: los porcentajes
// pasaban de 100 % porque `rankingHabitos` no estaba aislado por campaña, y el embudo usaba la
// plantilla del scope COMPLETO como denominador en vez de la audiencia propia de la campaña.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useRecognitionCampaign, useRecognitionCampaignLive, useWinnersExport,
  useCampaignSummaries, useCampaigns, getCampaignName, getFundamentalColor,
} from '@/api/recognition';
import type {
  RecognitionReportParams, WinnersExportFormat, ShineRankEntryDto, RecognitionModalidad, AdopcionDto,
  NominationRawExportRowDto, CampaignReportDto, RankedEntryDto, CampaignSummaryDto,
} from '@/api/recognition';
import { exportToExcel } from '@/utils/exportExcel';
import { usePersonNameResolver, useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import {
  ReportStatus, KpiRow, SuppressedNote, withResolvedNames, filtrosActivosDe, soloPersonaDe, conFiltrosDeTira,
} from '../reportsShared';
import type { KpiItem } from '../reportsShared';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import { DistribucionFundamental } from './DistribucionFundamental';
import { BloquePlantilla } from '../BloquePlantilla';
// `pctAdopcion` se REUSA del Diario, no se copia: es la misma regla («plantilla 0 = desconocida, no
// grupo vacío») y tenerla dos veces garantizaría que un día divergieran.
import { pctAdopcion } from './DailyTab';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import { Paginacion, usePaginacion } from '../Paginacion';
import { tasaDeParticipacion, formatTasaDisplay } from './CampaignsGeneralTab';
import { fetchNominacionesCrudasPaginado } from '../../fetchNominacionesCrudas';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellValue, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import { excelColors, argbACss } from '@/utils/excelTheme';
import { resolverFundamentalesConHabitos } from '../../fundamentalesHabitosMatrix';
import type { FundamentalConHabitosResueltos } from '../../fundamentalesHabitosMatrix';
import {
  SIN_DATO, buildDefinicionesSheet, buildDistribucionFundamentalSheet, buildEmbudoSheet,
  buildHojaSinDatos, buildRankingSheet, buildResumenEjecutivoSheet, buildTodosLosReconocimientosSheet,
} from './brillamosExcel';
import type {
  FilaRankingBrillamos, KpisBrillamos, MetadatosBrillamos, Traductor,
} from './brillamosExcel';

/**
 * Porcentaje de adopción de un segmento como FRACCIÓN 0-1 — el formato `0.00%` de Excel hace la
 * conversión, y así la celda sigue siendo un número ordenable en vez del texto `"50.00%"` que se
 * alineaba del lado contrario a los conteos de su misma columna.
 *
 * `null` = censo sin responder (contrato de `AdopcionDto`): cae al guion de «sin dato», nunca a un
 * 0 % que afirmaría que nadie de ese grupo reconoció.
 */
function fraccionDeAdopcion(dato: AdopcionDto | null | undefined): ExcelCellValue {
  const pct = pctAdopcion(dato);
  return pct === null ? SIN_DATO : pct / 100;
}

/** Detalle («N de M han nominado») de una fila de adopción; vacío si el censo no respondió. */
function detalleDeAdopcion(dato: AdopcionDto | null | undefined, t: TFunction<'recognition'>): string {
  if (pctAdopcion(dato) === null) return '';
  return t('reports.shine.adoptionHint', {
    reconocedores: dato!.reconocedores,
    plantilla: dato!.plantilla,
  });
}

/**
 * Los KPI del «Resumen Ejecutivo», ya resueltos a número (o al guion de «sin dato»). Cuanto la hoja
 * necesita saber de la campaña se resuelve aquí; `brillamosExcel.ts` solo los pinta.
 *
 * Objeto de opciones y no cinco parámetros posicionales: `summary`, `isSummarySuprimido` y
 * `tasaParticipacion` describen el mismo resumen y en posición serían fáciles de cruzar.
 */
function kpisDelLibro(opciones: {
  report: CampaignReportDto;
  summary: CampaignSummaryDto | undefined;
  isSummarySuprimido: boolean;
  tasaParticipacion: number | null;
  t: TFunction<'recognition'>;
}): KpisBrillamos {
  const { report, summary, isSummarySuprimido, tasaParticipacion, t } = opciones;
  // `plantilla <= 0` es DESCONOCIDO, no «grupo vacío» (contrato de `AdopcionDto`): `pctAdopcion` ya
  // devuelve `null` en ese caso y sirve de guarda para las tres filas que salen de este bloque.
  const totalAdopcion = report.adopcionTotal;
  const pctTotalAdopcion = pctAdopcion(totalAdopcion);
  const hayResumen = !!summary && !isSummarySuprimido;

  return {
    pares: report.pares ?? SIN_DATO,
    lideres: report.lideres ?? SIN_DATO,
    adopcionStaff: fraccionDeAdopcion(report.adopcionStaff),
    detalleAdopcionStaff: detalleDeAdopcion(report.adopcionStaff, t),
    adopcionFrontline: fraccionDeAdopcion(report.adopcionFrontline),
    detalleAdopcionFrontline: detalleDeAdopcion(report.adopcionFrontline, t),
    usuariosTotales: pctTotalAdopcion !== null ? totalAdopcion!.reconocedores : SIN_DATO,
    detalleUsuariosTotales: pctTotalAdopcion !== null
      ? t('reports.shine.adoptionTotalHint', {
          pct: `${pctTotalAdopcion.toFixed(2)}%`, plantilla: totalAdopcion!.plantilla,
        })
      : '',
    // «Sin actividad» se DERIVA de las dos cifras de adopción, no viaja como campo propio: así no
    // puede desincronizarse de ellas.
    sinActividad: pctTotalAdopcion !== null
      ? Math.max(0, totalAdopcion!.plantilla - totalAdopcion!.reconocedores)
      : SIN_DATO,
    reconocimientosEnviados: hayResumen ? summary!.reconocimientos : SIN_DATO,
    totalIndividual: report.totalIndividual ?? SIN_DATO,
    totalGrupal: report.totalGrupal ?? SIN_DATO,
    nominadosUnicos: hayResumen ? (summary!.nominadosUnicos ?? SIN_DATO) : SIN_DATO,
    participantes: hayResumen ? (summary!.participantes ?? SIN_DATO) : SIN_DATO,
    // Fracción 0-1: `tasaDeParticipacion` devuelve el porcentaje ya multiplicado por cien.
    tasaParticipacion: !isSummarySuprimido && tasaParticipacion != null
      ? tasaParticipacion / 100
      : SIN_DATO,
    plantillaTotal: summary?.audiencia ?? totalAdopcion?.plantilla ?? SIN_DATO,
    plantillaStaff: report.adopcionStaff?.plantilla ?? SIN_DATO,
    plantillaFrontline: report.adopcionFrontline?.plantilla ?? SIN_DATO,
  };
}

/**
 * De `ShineRankEntryDto` (lo que manda el backend) a la fila que pinta `buildRankingSheet`. El
 * Fundamental viaja como ID y se resuelve aquí contra el catálogo CON inactivos, en el idioma
 * activo: la columna enseña Fundamentales HISTÓRICOS y con el catálogo activo uno retirado caería
 * a su GUID.
 */
function filasDeRanking(
  entradas: ShineRankEntryDto[], nombreDeFundamental: (id: string) => string, traducir: Traductor,
): FilaRankingBrillamos[] {
  return entradas.map((entrada): FilaRankingBrillamos => ({
    nombre: entrada.nombre,
    isSuprimido: !!entrada.suprimido,
    email: entrada.email,
    employeeId: entrada.employeeId,
    area: entrada.area,
    ouName: entrada.ouName,
    site: entrada.site,
    fundamental: entrada.fundamentalId ? nombreDeFundamental(entrada.fundamentalId) : null,
    count: entrada.count,
    crossOu: entrada.crossOuCount == null
      ? SIN_DATO
      : traducir('excel.crossOuCount', { count: entrada.crossOuCount }),
    tipoDominante: entrada.tipoDominante,
  }));
}

/**
 * Las 7 hojas del libro, en el orden de la plantilla del cliente. Cada hoja de detalle cae a su
 * hoja de hueco documentado cuando la campaña está genuinamente sin datos (sin reconocimientos, o
 * sin nadie que haya reconocido), nunca a una pestaña vacía ni a cifras fabricadas.
 */
function construirLibroBrillamos(opciones: {
  traducir: Traductor;
  t: TFunction<'recognition'>;
  report: CampaignReportDto;
  metadatos: MetadatosBrillamos;
  summary: CampaignSummaryDto | undefined;
  isSummarySuprimido: boolean;
  tasaParticipacion: number | null;
  resolvedRanking: ShineRankEntryDto[];
  resolvedTopReconocedores: ShineRankEntryDto[];
  nominaciones: NominationRawExportRowDto[];
  isTruncado: boolean;
  fundamentalesConHabitos: FundamentalConHabitosResueltos[];
  rankingHabitosTodos: RankedEntryDto[];
  padreDelHabito: Map<string, string>;
  nombreDeFundamental: (id: string) => string;
  colorDeFundamental: (id: string) => string;
  nombreDeHabito: (id: string) => string;
}): ExcelSheetSpec[] {
  const {
    traducir, t, report, metadatos, summary, isSummarySuprimido, tasaParticipacion, resolvedRanking,
    resolvedTopReconocedores, nominaciones, isTruncado, fundamentalesConHabitos, rankingHabitosTodos,
    padreDelHabito, nombreDeFundamental, colorDeFundamental, nombreDeHabito,
  } = opciones;

  const topFundamentales = report.topFundamentales ?? [];
  const hojaDistribucion = topFundamentales.length > 0
    ? buildDistribucionFundamentalSheet({
        traducir,
        topFundamentales,
        total: report.totalNominaciones,
        nombreDeFundamental,
        colorDeFundamental,
        rankingHabitosTodos,
        padreDelHabito,
        nombreDeHabito,
      })
    : buildHojaSinDatos(
        traducir('excel.sheetDistribution'),
        traducir('excel.distributionTitle'),
        traducir('excel.distributionEmpty'),
      );

  const embudo = report.embudo ?? [];
  const hojaEmbudo = embudo.length > 0
    ? buildEmbudoSheet(traducir, embudo)
    : buildHojaSinDatos(
        traducir('excel.sheetFunnel'), traducir('excel.funnelTitle'), traducir('excel.funnelEmpty'),
      );

  const hojaTopReconocedores = resolvedTopReconocedores.length > 0
    ? buildRankingSheet(
        traducir,
        traducir('excel.sheetTopRecognizers'),
        traducir('excel.topRecognizersTitle'),
        filasDeRanking(resolvedTopReconocedores, nombreDeFundamental, traducir),
      )
    : buildHojaSinDatos(
        traducir('excel.sheetTopRecognizers'),
        traducir('excel.topRecognizersTitle'),
        traducir('excel.topRecognizersEmpty'),
      );

  return [
    buildResumenEjecutivoSheet(
      traducir, metadatos, kpisDelLibro({ report, summary, isSummarySuprimido, tasaParticipacion, t }),
    ),
    buildDefinicionesSheet(traducir, isTruncado),
    hojaDistribucion,
    hojaEmbudo,
    buildRankingSheet(
      traducir,
      traducir('excel.sheetTopRecognized'),
      traducir('excel.topRecognizedTitle'),
      filasDeRanking(resolvedRanking, nombreDeFundamental, traducir),
    ),
    hojaTopReconocedores,
    buildTodosLosReconocimientosSheet(traducir, nominaciones, fundamentalesConHabitos),
  ];
}

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
export function ShineTab(
  { params, canExportData = true }: Readonly<{ params: RecognitionReportParams; canExportData?: boolean }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const [format, setFormat] = useState<WinnersExportFormat>('cascaded');
  // Fix round 1, hallazgo 1+2+3 — `handleExportShine` ahora hace hasta 25 idas y vueltas HTTP
  // (`fetchNominacionesCrudasPaginado`) antes de armar el workbook; sin este estado, un fallo a
  // mitad de camino quedaba como una promesa rechazada sin manejar (ningún mensaje, ningún log) y
  // un doble clic disparaba dos paginaciones completas en paralelo. Mismo patrón que
  // `exportandoNominaciones`/`exportError` en `AdminWinners.tsx`.
  const [exportandoShine, setExportandoShine] = useState(false);
  const [exportShineError, setExportShineError] = useState(false);
  // Fix final (revisión de rama) — mismo hallazgo que arriba, pero en `handleExportWinners`: la
  // migración xlsx→exceljs volvió async a `exportToExcel` y ese handler quedó sin try/catch. Estado
  // PROPIO (no se reusa `exportandoShine`/`exportShineError`): son dos descargas independientes —
  // "Exportar Brillamos" y "Exportar ganadores" — y compartir el flag bloquearía un botón por el
  // fallo del otro.
  //
  // 🔴 Fix ronda 2 (revisión) — el `try/catch` NO bastaba aquí: `winners` es un `useQuery({ enabled:
  // false, retry: 0 })` (`useWinnersExport`, `hooks.ts`) y `refetch()` de TanStack Query v5 por
  // defecto tiene `throwOnError: false` — ante un 401/500 RESUELVE con `{ data: undefined, isError:
  // true, error }`, NUNCA rechaza la promesa. El `try/catch` nunca se disparaba: `res.data ?? []`
  // caía a `[]`, `exportToExcel([], …)` corría "bien" y el usuario se llevaba un `.xlsx` vacío con
  // apariencia de éxito — peor que el bug original, que al menos era detectable como fallo. Se pide
  // `refetch({ throwOnError: true })` para que el fallo SÍ rechace la promesa y el `try/catch` de
  // abajo lo atrape de verdad (mismo criterio que `handleExportShine`, cuyas llamadas SÍ rechazan
  // porque no pasan por `refetch`).
  const [exportandoWinners, setExportandoWinners] = useState(false);
  const [exportWinnersError, setExportWinnersError] = useState(false);

  // T-10 — el tab es POR CAMPAÑA (confirmado con Adriana, 18-ago-2026), y hasta ahora no había forma
  // de elegir una: `params` solo trae ou/fechas/fundamental, así que el backend recibía `campaignId`
  // vacío, devolvía el agregado del scope con el nombre literal «Brillamos» y los tres KPIs que
  // dependen del resumen por campaña pintaban «—» para siempre. El selector es la pieza que faltaba.
  const [campaignId, setCampaignId] = useState('');
  /*
   * Maqueta — select «Filtro Brillamos» sobre el Ranking de Nominados: todos / solo persona única /
   * cross-OU. Vive en el TAB y no en la barra de filtros global porque es específico de esta tabla:
   * la barra la comparten siete pestañas y ninguna otra sabe qué hacer con esto.
   *
   * ⚠️ Este filtro sale solo del prototipo, no de la Sección 7.
   */
  const [modalidad, setModalidad] = useState<RecognitionModalidad>('todos');
  /*
   * Tamaño del ranking de nominados. Manda el `top` de la petición (50 por omisión en este tab,
   * techo 200), no un recorte del cliente. Control de TAMAÑO y no de páginas: el backend no tiene
   * offset.
   */
  /*
   * Filas que se PIDEN. Fijo, no un control en pantalla — el mismo criterio que el tab Diario desde el
   * 25-ago: las tablas paginan en cliente, así que un selector que dispare consulta por cada cambio de
   * página sobra. El valor es el «Top 200» de las hojas «Definiciones» del cliente, que el handler
   * clampea ahí de todos modos.
   */
  const TOP_PEDIDO = 200;
  const campaigns = useCampaigns(undefined, 1, 100);
  const listaCampanas = useMemo(() => campaigns.data?.items ?? [], [campaigns.data]);

  const scopedParams: RecognitionReportParams = { ...params, campaignId, modalidad, top: TOP_PEDIDO };

  // Nombre legible de la campaña elegida, resuelto contra la MISMA lista que llena el selector.
  const nombreDeCampana = useMemo(() => {
    const elegida = listaCampanas.find((c) => c.id === campaignId);
    return elegida ? getCampaignName(elegida, lang) : '';
  }, [listaCampanas, campaignId, lang]);

  /**
   * La consulta se habilita con campaña elegida O con alguno de los filtros de la tira puestos.
   *
   * 🔴 Antes solo la campaña la encendía, y por eso el selector de Fundamental de la tira —que este
   * tab SÍ muestra— no hacía nada mientras no hubiera campaña: no es que filtrara mal, es que no se
   * pedía nada. El backend responde sin campaña desde que `porFundamental` fuerza la vía de recibos.
   */
  const hayFiltroDeTira = Boolean(params.fundamentalId || params.personaId);
  const consultaHabilitada = Boolean(campaignId) || hayFiltroDeTira;
  const filtrosActivos = useMemo(() => filtrosActivosDe(params), [params]);
  /**
   * 2026-09-04 — solo la persona: a diferencia de «Total de plantilla» (que no se acota ni por
   * Fundamental ni por persona), los cuatro KPIs de arriba («Reconocimientos realizados», «Personas
   * únicas reconocidas», «Personas únicas que reconocen», «Tasa de participación») SÍ se acotan por
   * Fundamental desde que `getCampaignSummary` volvió a recibir ese parámetro — el único filtro que
   * el endpoint `campaign-summary` no acepta es la persona. Mismo patrón que el embudo de `DailyTab`.
   */
  const soloPersona = useMemo(() => soloPersonaDe(filtrosActivos), [filtrosActivos]);

  const q = useRecognitionCampaign(scopedParams, consultaHabilitada);
  const report = q.data;
  // Tarea 15 — el lote de ids a resolver incluye también `topReconocedores`: el backend manda
  // `Nombre: entrada.ReceptorId` para esas filas (mismo patrón crudo que `ranking`, ver
  // `GetRecognitionCampaignQueryHandler.cs`), así que sin sumarlos aquí el export de «Top
  // Reconocedores» repetiría el bug de GUID crudo que T-10 ya encontró y corrigió para el ranking.
  const nomineeIds = useMemo(() => {
    const nominados = report?.ranking.map((entrada) => entrada.id) ?? [];
    const reconocedores = report?.topReconocedores?.map((entrada) => entrada.id) ?? [];
    // Punto 6 (revisión final) — `params.personaId` se suma al lote: sin esto, «Filtros aplicados»
    // del export mostraría el id crudo si la persona elegida no aparece en ningún ranking.
    const persona = params.personaId ? [params.personaId] : [];
    return [...nominados, ...reconocedores, ...persona];
  }, [report, params.personaId]);
  const { resolvePersonName } = usePersonNameResolver(nomineeIds);
  const { resolveOuName } = useOuNameResolver();
  const resolvedRanking = useMemo(
    () => (report ? withResolvedNames(report.ranking, resolvePersonName) : []),
    [report, resolvePersonName],
  );
  const resolvedTopReconocedores = useMemo(
    () => (report?.topReconocedores ? withResolvedNames(report.topReconocedores, resolvePersonName) : []),
    [report, resolvePersonName],
  );

  const live = useRecognitionCampaignLive(scopedParams, Boolean(campaignId));
  const winners = useWinnersExport(format, scopedParams);

  // E4 — los 5 KPIs unificados de la spec 4.3 (Reconocimientos realizados / Personas únicas
  // reconocidas / Personas únicas que reconocen / Tasa de participación / Total de plantilla) salen
  // de `CampaignSummaryDto` (mismo endpoint GetCampaignSummary que ya consume `CampaignsGeneralTab`),
  // no de `GetCampaign`/`useRecognitionCampaignLive`: esos KPIs "de hoy" (nominaciones/OUs activas/
  // nominaciones en vivo/días restantes) no correspondían a ninguno de los 5 de la spec.
  // 2026-09-04 — el resumen vuelve a acotarse por Fundamental: el ranking de abajo ya lo hacía y los
  // 5 KPIs no, así que quedaban leyendo la campaña completa encima de un ranking filtrado. `Audiencia`
  // es la única cifra que el backend deja sin acotar (ver nota de `AvisoFiltroNoAplica` más abajo).
  const [summaryQuery] = useCampaignSummaries(campaignId ? [campaignId] : [], params.fundamentalId);
  const summary = summaryQuery?.data;
  const isSummarySuprimido = summary?.suprimido ?? false;
  const tasaParticipacion = tasaDeParticipacion(summary?.participantes, summary?.audiencia);

  // Tabla «Ranking Daily Habits» en pantalla (Tarea 14b: en el Excel ya no es su propia hoja, se
  // pliega en «Distribución Fundamental»). El backend manda el ID en `nombre` porque el catálogo de
  // hábitos vive en SurveyService y AnalyticsService no lee su base (regla de oro #2); aquí se
  // traduce con el catálogo que la pantalla ya carga. Un id que el catálogo no conozca se
  // muestra TAL CUAL en vez de desaparecer: un hábito retirado sigue siendo un dato real.
  // `isLoading` (fix round 1, hallazgo 5) — gatea el botón de exportar (más abajo): sin esto, un
  // clic mientras la query del catálogo aún no resuelve arma `buildTodosLosReconocimientosSheet`
  // con `catalogoReporteria` en `undefined`, y la matriz de hábitos sale con CERO columnas en
  // silencio, sin que nadie lo note.
  // `isError` (fix final, hallazgo catálogo-falla-en-silencio) — mismo riesgo que `isLoading`, pero
  // cuando la query FALLA (no solo tarda): `isLoading` cae a `false` con `data` en `undefined`, así
  // que sin esto el botón se reactivaba con el catálogo vacío y `nombreDeFundamental` caía en su
  // fallback de "Fundamental retirado" para CADA fila de "Top Reconocidos"/"Top Reconocedores", la
  // matriz de hábitos de "Todos los reconocimientos" salía con CERO columnas, y "Distribución
  // Fundamental" perdía sus nombres — un archivo con datos afirmativamente incorrectos, no un hueco.
  const {
    data: catalogoReporteria, isLoading: catalogoCargando, isError: catalogoConError,
  } = useFundamentalsForReporting();
  /**
   * Nombre del hábito. Del catálogo CON inactivos por el mismo motivo que los Fundamentales: esta
   * tabla enseña hábitos HISTÓRICOS y con el catálogo activo uno retirado caería a su GUID.
   */
  const nombreDeHabito = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      for (const h of f.dailyHabits ?? []) {
        porId.set(h.id, h.names?.[lang] ?? h.names?.en ?? h.code ?? h.id);
      }
    }
    return (id: string) => porId.get(id) ?? id;
  }, [catalogoReporteria, lang]);

  /**
   * Nombre del Fundamental de cada fila. Del catálogo CON inactivos: la columna enseña Fundamentales
   * HISTÓRICOS y con el catálogo activo un Fundamental retirado caería a su GUID — el mismo defecto
   * que ya se corrigió en el bloque de barras.
   */
  const nombreDeFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      porId.set(f.id, f.names?.[lang] ?? f.names?.en ?? f.code ?? f.id);
    }
    // Sin nombre NO se enseña el id: se rotula como retirado, igual que el bloque de barras.
    return (id: string) => porId.get(id) ?? t('reports.daily.retiredFundamental');
  }, [catalogoReporteria, lang, t]);

  /**
   * Bug de producción 2026-08-26 — color de cada Fundamental para la gráfica de barras del export,
   * el MISMO color guardado en catálogo que ya usa `<DistribucionFundamental>` en pantalla
   * (`getFundamentalColor`, `f.color` de la base con fallback a la paleta por índice) — no un color
   * único inventado para el export.
   */
  const colorDeFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    (catalogoReporteria ?? []).forEach((f, indice) => {
      porId.set(f.id, getFundamentalColor(f as never, indice));
    });
    return (id: string) => porId.get(id) ?? argbACss(excelColors.headerTablaFondo);
  }, [catalogoReporteria]);

  /**
   * Fundamental padre de cada hábito, del catálogo CON inactivos. Es la misma relación que usa el
   * bloque de barras para su segundo nivel; aquí alimenta la columna «Fundamental» de la tabla de
   * hábitos y su filtro propio.
   */
  const padreDelHabito = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      for (const h of f.dailyHabits ?? []) porId.set(h.id, f.id);
    }
    return porId;
  }, [catalogoReporteria]);

  /**
   * Filtro propio de la tabla de hábitos. NO se toca el filtro global de Fundamental de la barra
   * superior: ese acota TODO el reporte (KPIs, ranking de nominados, distribución) y aquí la pregunta
   * es solo «de esta tabla, enséñame los hábitos de tal Fundamental». Mezclarlos haría que ordenar
   * una tabla cambiara las cifras de arriba.
   */
  const [habitoFundFiltro, setHabitoFundFiltro] = useState<string>('');

  const rankingHabitosTodos = report?.rankingHabitos ?? [];

  /** Fundamentales presentes en la tabla — solo esos se ofrecen: filtrar a un resultado vacío es ruido. */
  const fundamentalesConHabitos = useMemo(() => {
    const ids = new Set<string>();
    for (const h of rankingHabitosTodos) {
      const padre = padreDelHabito.get(h.id);
      if (padre) ids.add(padre);
    }
    return [...ids]
      .map((id) => ({ id, nombre: nombreDeFundamental(id) }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, i18n.language));
  }, [rankingHabitosTodos, padreDelHabito, nombreDeFundamental, i18n.language]);

  const rankingHabitos = habitoFundFiltro
    ? rankingHabitosTodos.filter((h) => padreDelHabito.get(h.id) === habitoFundFiltro)
    : rankingHabitosTodos;

  /*
   * Las CUATRO tarjetas de adopción de la maqueta, acotadas a la campaña por el backend.
   *
   * 🔴 `null` NO es 0 %. Un 0 % afirmaría que nadie de ese grupo nominó; `null` dice que no se pudo
   * calcular (el padrón no respondió, o no hay censo). Mismo criterio que el Diario, y la razón por la
   * que se reusa `pctAdopcion` en vez de recalcular aquí.
   */
  const tarjetasDeAdopcion = useMemo((): KpiItem[] => {
    const numero = (n: number) => n.toLocaleString(i18n.language);

    const deSegmento = (etiqueta: string, dato: AdopcionDto | null | undefined, color: string): KpiItem => {
      const pct = pctAdopcion(dato);
      if (pct === null) return { label: etiqueta, value: '—', color };
      return {
        label: etiqueta,
        // Fix (2 decimales) — ver el comentario de `kpiAdopcionSegmento`: mismo `.toFixed(2)` para
        // que la tarjeta en pantalla y su fila del Excel muestren el mismo formato.
        value: `${pct.toFixed(2)}%`,
        color,
        sub: t('reports.shine.adoptionHint', {
          reconocedores: numero(dato!.reconocedores),
          plantilla: numero(dato!.plantilla),
        }),
        subTone: 'muted',
      };
    };

    const total = report?.adopcionTotal;
    const pctTotal = pctAdopcion(total);
    // «Sin actividad» se DERIVA: plantilla − nominadores. No es un campo propio para que no pueda
    // desincronizarse de las dos cifras de las que sale.
    const sinActividad = total && total.plantilla > 0
      ? Math.max(0, total.plantilla - total.reconocedores)
      : null;

    return [
      deSegmento(t('reports.shine.adoptionStaff'), report?.adopcionStaff, '#342F82'),
      deSegmento(t('reports.shine.adoptionFrontline'), report?.adopcionFrontline, '#F08521'),
      {
        label: t('reports.shine.adoptionTotal'),
        value: total && total.plantilla > 0 ? numero(total.reconocedores) : '—',
        sub: pctTotal !== null
          ? t('reports.shine.adoptionTotalHint', {
              pct: `${pctTotal.toFixed(2)}%`, plantilla: numero(total!.plantilla),
            })
          : undefined,
        subTone: 'muted',
      },
      {
        label: t('reports.shine.noActivity'),
        value: sinActividad !== null ? numero(sinActividad) : '—',
        color: 'var(--cp-red)',
        sub: t('reports.shine.noActivityHint'),
        subTone: 'muted',
      },
    ];
  }, [report, i18n.language, t]);

  // Una paginación por tabla, independientes: la maqueta pinta un pie en cada una.
  const pagNominados = usePaginacion(resolvedRanking);
  const pagHabitos = usePaginacion(rankingHabitos);

  const isEmpty = !!report && report.totalNominaciones === 0 && report.ranking.length === 0;

  async function handleExportShine() {
    if (!report) return;

    // Fix round 1, hallazgo 1+2+3 — `handleExportShine` hace hasta 25 idas y vueltas HTTP
    // (`fetchNominacionesCrudasPaginado`) para «Todos los reconocimientos»: sin try/catch un 401/500
    // a mitad de camino rechazaba la promesa sin manejar (ningún mensaje, ningún log), y sin
    // `exportandoShine` el botón (más abajo) seguía habilitado durante toda la descarga, invitando
    // un doble clic que dispara dos paginaciones completas en paralelo. Mismo patrón que
    // `exportarNominacionesCrudas` en `AdminWinners.tsx`.
    setExportandoShine(true);
    setExportShineError(false);
    try {
      // El export CRUDO paginado alimenta «Todos los reconocimientos». `truncado`: si el recorrido
      // se cortó por el TOPE (no por página vacía), el archivo es INCOMPLETO — se avisa con una
      // fila extra en «Definiciones», nunca en silencio.
      const { filas: nominacionesCrudas, truncado: nominacionesTruncadas } = (
        await fetchNominacionesCrudasPaginado(campaignId)
      );

      const sheets = construirLibroBrillamos({
        traducir: (clave: string, opciones?: Record<string, unknown>) => (
          t(`reports.shine.${clave}`, opciones ?? {})
        ),
        t,
        report,
        metadatos: {
          // NO se usa `report.nombre`: el backend hace `Nombre: query.CampaignId ?? "Brillamos"`
          // (`GetRecognitionCampaignQueryHandler.cs`), así que ese campo es el GUID de la campaña,
          // nunca su nombre legible — el mismo defecto que ya se evitó en pantalla.
          nombreDeCampana,
          periodo: [params.startDate, params.endDate].filter(Boolean).join(' – ') || SIN_DATO,
          filtrosAplicados: conFiltrosDeTira(
            params.ouIds?.length ? params.ouIds.map(resolveOuName).join(', ') : SIN_DATO,
            params,
            nombreDeFundamental,
            resolvePersonName,
            // Los dos rótulos de la tira también viajan traducidos: el libro entero sale en el
            // idioma de quien descarga, y dejarlos en español partiría esa línea a medio idioma.
            { fundamental: t('reports.filters.fundamental'), persona: t('reports.filters.person') },
          ),
          generado: new Date().toLocaleString(i18n.language),
        },
        summary,
        isSummarySuprimido,
        tasaParticipacion,
        resolvedRanking,
        resolvedTopReconocedores,
        nominaciones: nominacionesCrudas,
        isTruncado: nominacionesTruncadas,
        // La matriz de hábitos se filtra a Fundamentales/Hábitos ACTIVOS: el backend solo emite una
        // clave por Daily Habit activo al momento del export, así que una columna fija para uno
        // retirado saldría siempre en blanco y se leería como «nadie lo marcó nunca».
        fundamentalesConHabitos: resolverFundamentalesConHabitos(catalogoReporteria, lang),
        rankingHabitosTodos,
        padreDelHabito,
        nombreDeFundamental,
        colorDeFundamental,
        nombreDeHabito,
      });

      await exportMultiSheetExcel({
        // Nombre de la campaña, no su id crudo (bug QA 2026-09-10): sin esto, el archivo descargado
        // se identificaba solo por un GUID. Mismo criterio de sanitizado que `LeadersTab.tsx`.
        fileName: nombreDeCampana
          ? `reporteria-brillamos-${nombreDeCampana.replace(/[^\p{L}\p{N}]+/gu, '-')}`
          : 'reporteria-brillamos',
        sheets,
      });
    } catch (error: unknown) {
      // Si falla, se DICE (mismo criterio que `AdminWinners.tsx`): sin esto, la promesa rechazada
      // no dejaba mensaje ni log, y quien exporta no tenía forma de saber si el archivo se generó.
      // Fix round 2 — el error también se loguea (guard DEV, mismo patrón que
      // `AdminWinners.tsx`/`CampaignRowActions.tsx`/`LeadersTab.tsx`): el banner le dice al usuario
      // QUE falló, pero sin esto un 401 y un 500 eran indistinguibles después del hecho.
      if (import.meta.env.DEV) console.error('No se pudo generar el export de Brillamos Juntos:', error);
      setExportShineError(true);
    } finally {
      setExportandoShine(false);
    }
  }

  async function handleExportWinners() {
    setExportandoWinners(true);
    setExportWinnersError(false);
    try {
      // `throwOnError: true` — ver el comentario junto a `exportandoWinners`/`exportWinnersError`:
      // sin esto un 401/500 no rechaza la promesa y este `try/catch` nunca se dispara.
      const res = await winners.refetch({ throwOnError: true });
      const list = res.data ?? [];
      const rows = list.map((w) => ({
        [t('reports.shine.colPos')]: w.posicion ?? '',
        [t('reports.shine.colName')]: w.nombre,
        [t('reports.shine.colOu')]: w.ouName ?? '',
        [t('reports.shine.colCountry')]: w.country ?? '',
        [t('reports.shine.colBadge')]: w.badgeId ?? '',
        [t('reports.shine.colCrossed')]: w.origenCruzado ? t('reports.common.yes') : t('reports.common.no'),
      }));
      // Nombre de la campaña, no su id crudo (bug QA 2026-09-10) — mismo criterio que el export
      // multi-hoja de arriba.
      await exportToExcel(
        rows,
        nombreDeCampana
          ? `brillamos-winners-${format}-${nombreDeCampana.replace(/[^\p{L}\p{N}]+/gu, '-')}`
          : `brillamos-winners-${format}`,
      );
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export de ganadores de Brillamos:', error);
      setExportWinnersError(true);
    } finally {
      setExportandoWinners(false);
    }
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-star" aria-hidden="true" /> {t('reports.shine.title')}
          </div>
          <div className="au-card-sub">{t('reports.shine.subtitle')}</div>
        </div>
      </div>

      {/* T-10 — el selector que faltaba. Sin campaña elegida el backend recibía `campaignId` vacío y
          devolvía el agregado del SCOPE bajo el nombre literal «Brillamos»: los KPIs por campaña
          quedaban en «—» y el ranking mezclaba a gente de todas. */}
      <div className="rr-actions">
        <select
          className="au-cascade-select"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          aria-label={t('reports.shine.campaignSelector')}
        >
          <option value="">{t('reports.shine.campaignSelector')}…</option>
          {listaCampanas.map((c) => (
            <option key={c.id} value={c.id}>{getCampaignName(c, lang)}</option>
          ))}
        </select>

        {/* Maqueta — «Filtro Brillamos». Acota el RANKING; los KPIs de la campaña salen de otro
            endpoint y no se filtran, igual que con el Fundamental. */}
        <select
          className="au-cascade-select"
          value={modalidad}
          onChange={(e) => setModalidad(e.target.value as RecognitionModalidad)}
          aria-label={t('reports.shine.modalidadSelector')}
        >
          <option value="todos">{t('reports.shine.modalidadTodos')}</option>
          <option value="personaUnica">{t('reports.shine.modalidadPersonaUnica')}</option>
          <option value="crossOu">{t('reports.shine.modalidadCrossOu')}</option>
        </select>
      </div>

      {!consultaHabilitada ? (
        /*
         * Mismo criterio que «Históricos»: se pide elegir en vez de pintar los números del scope
         * bajo etiquetas que dicen «de la campaña».
         *
         * El mensaje se retira en cuanto CUALQUIER filtro de la tira (Fundamental o persona)
         * habilita la consulta sin campaña — no solo cuando se elige una.
         */
        <div className="rr-state">{t('reports.shine.pickCampaign')}</div>
      ) : (
      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {report && (
          <>
            {(report.suprimido || live.data?.suprimido || isSummarySuprimido) && <SuppressedNote />}

            {/*
              Banner naranja de la maqueta. Dice de dónde vienen los filtros de este panel: los de la
              tira de arriba se HEREDAN, y dentro del panel solo se elige campaña y modalidad. Sin él,
              alguien que no ve la tira (porque hizo scroll) no sabe que hay un recorte aplicado.

              «Limpiar todo» solo aparece si hay algo que limpiar: un botón que no hace nada invita a
              pulsarlo y a dudar de la pantalla cuando no pasa nada.
            */}
            <p className="rr-filtros-aplicados">
              <i className="fas fa-filter" aria-hidden="true" />
              <span>
                <strong>{t('reports.shine.appliedFilters')}</strong>{' '}
                {t('reports.shine.appliedFiltersValue')}
              </span>
              {(campaignId || modalidad !== 'todos') && (
                <button
                  type="button"
                  className="rr-filtros-aplicados-limpiar"
                  onClick={() => { setCampaignId(''); setModalidad('todos'); }}
                >
                  {t('reports.shine.clearAll')}
                </button>
              )}
            </p>

            {/*
              🔴 NO se usa `report.nombre`: el backend hace `Nombre: query.CampaignId ?? "Brillamos"`,
              así que con campaña elegida devuelve el ID y la pantalla imprimía el GUID crudo
              (`8ee48bc749d8…`, visto en QA el 24-ago). El nombre ya lo tenemos aquí: es el mismo de
              la lista que alimenta el selector. Se cae a `report.nombre` solo si la campaña elegida
              ya no está en la lista, para no dejar el bloque vacío.
            */}
            <div className="au-card-sub rr-block-title">{nombreDeCampana}</div>

            {/*
              🔴 LOS CINCO KPIs DE LA SPEC 4.3 SE QUEDAN, aunque la maqueta pinte solo cuatro.
              «Personas únicas que reconocen», «Tasa de participación» y «Total de plantilla» son
              requisito de la Especificación, y en este proyecto la Especificación MANDA sobre lo que
              se pide en la maqueta o en UAT — ya invalidó dos peticiones por lo mismo. Quitarlos para
              parecerse a la maqueta habría retirado tres KPIs pedidos por escrito.
              Los dos de la maqueta que sí faltaban («Días restantes», «OUs activas») ya estaban, y la
              fila de adopción de abajo es lo genuinamente nuevo.

              2026-09-04 — «Total de plantilla» sale de esta fila (va justo debajo, con su propio
              aviso): es el único de los cinco que el backend NO acota por Fundamental. Los otros
              cuatro sí lo hacen desde que `getCampaignSummary` volvió a recibir `fundamentalId`
              (`ShineTab.tsx`, `hooks.ts`), así que dejan de necesitar el aviso de Fundamental — la
              «Tasa de participación» se recalcula sola contra la misma `Audiencia` sin acotar, que es
              precisamente lo que «participación» quiere decir (sobre la audiencia convocada).

              Con PERSONA sí queda un hueco real: `campaign-summary` no acepta `personaId` (decisión de
              producto — con una sola persona, «Personas únicas reconocidas» daría 1 y la «tasa de
              participación» compararía a esa persona contra la audiencia completa bajo una etiqueta
              que dice «participación», el mismo criterio por el que el embudo de `DailyTab` se calla
              con persona elegida). De ahí el aviso propio con `soloPersona`, no `filtrosActivos`: con
              solo Fundamental puesto los cuatro SÍ filtran y no deben avisar nada.
            */}
            <AvisoFiltroNoAplica filtros={soloPersona} motivo="resumenCampana" />
            <KpiRow
              items={[
                {
                  label: t('reports.shine.recognitionsMade'),
                  value: summary && !isSummarySuprimido ? summary.reconocimientos : '—',
                  color: 'var(--cp-purple)',
                },
                {
                  /*
                   * T-10 — del RESUMEN de la campaña, no de `report.nominadosUnicos`.
                   *
                   * Ese campo es del SCOPE y de todos los orígenes: era el «75» que en UAT se leyó
                   * como «personas reconocidas en la campaña». Un número real bajo la etiqueta
                   * equivocada, que es peor que un hueco — el hueco se pregunta, el número se cree.
                   */
                  label: t('reports.shine.uniqueRecognized'),
                  value: summary && !isSummarySuprimido ? (summary.nominadosUnicos ?? '—') : '—',
                  color: 'var(--cp-cyan)',
                },
                {
                  /*
                   * T-10 — ya no es un hueco: `participantes` del resumen SON los emisores únicos de
                   * la campaña. Estaba calculado desde C2 y este tab lo ignoraba, pintando «—» al
                   * lado de un dato que ya viajaba en la respuesta que la pantalla pedía.
                   */
                  label: t('reports.shine.uniqueRecognizers'),
                  value: summary && !isSummarySuprimido ? (summary.participantes ?? '—') : '—',
                  color: 'var(--cp-orange)',
                },
                {
                  label: t('reports.shine.participationRate'),
                  value: !isSummarySuprimido && tasaParticipacion != null ? formatTasaDisplay(tasaParticipacion, false) : '—',
                  color: 'var(--cp-green)',
                },
                /*
                 * Los DOS de la maqueta que faltaban: «Días restantes» y «OUs activas».
                 *
                 * Se habían dejado fuera al cerrar E4, cuando la fila se alineó a los CINCO KPIs
                 * unificados de la spec 4.3 y estos dos —que son «de hoy», no del periodo— no
                 * correspondían a ninguno. La maqueta sí los pinta, y son aditivos: no desplazan a
                 * ninguno de los cinco ni cambian lo que miden.
                 *
                 * 🔴 No hace falta pedir nada: salen de `campaign-live`, que este tab YA consulta
                 * para el mismo campaignId. Estaban llegando y se descartaban.
                 *
                 * `DiasRestantes` es null cuando la campaña no tiene fecha de fin —«Fundamentales»
                 * no la tiene, es permanente— y eso NO es un cero: se pinta «—», porque «quedan 0
                 * días» diría que cerró hoy.
                 */
                {
                  label: t('reports.shine.daysLeft'),
                  value: live.data?.diasRestantes ?? '—',
                  color: 'var(--cp-purple)',
                },
                {
                  label: t('reports.shine.activeOus'),
                  value: live.data && !live.data.suprimido ? live.data.ousActivas : '—',
                  color: 'var(--cp-cyan)',
                },
              ]}
            />

            {/*
              «Total de plantilla» (Audiencia CONVOCADA por la campaña) es el único de los cinco KPIs
              de la spec 4.3 que el backend deja sin acotar por Fundamental ni por persona — de ahí su
              propio aviso, pegado SOLO a él y no a toda la fila de arriba, que sí responde al filtro.
            */}
            <AvisoFiltroNoAplica filtros={filtrosActivos} motivo="audienciaCampana" />
            <KpiRow
              items={[
                {
                  label: t('reports.shine.totalHeadcount'),
                  value: summary?.audiencia ?? '—',
                  color: 'var(--cp-red)',
                },
              ]}
            />

            {/*
              Bloque de plantilla, SEGUNDA instancia (la primera va en el armazón). No es copia-pega:
              su nota dice que estas cifras son el DENOMINADOR de la adopción que va justo debajo, que
              es para lo que sirve ahí. Sin esa nota, repetir el mismo bloque no aportaría nada.
            */}
            <BloquePlantilla
              ouIds={params.ouIds}
              nota={t('reports.headcount.notaAdopcion')}
            />

            {/* Las cuatro de adopción de la maqueta, acotadas a ESTA campaña. */}
            <KpiRow items={tarjetasDeAdopcion} />

            {report.ranking.length === 0 ? (
              <div className="rr-state">{t('reports.common.empty')}</div>
            ) : (
              <div className="rr-table-wrap">
                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>{t('reports.shine.colPos')}</th>
                      <th>{t('reports.shine.colName')}</th>
                      <th>{t('reports.shine.colOu')}</th>
                      <th>{t('reports.shine.colBu')}</th>
                      <th>{t('reports.shine.colDept')}</th>
                      <th>{t('reports.daily.colFundamental')}</th>
                      <th>{t('reports.shine.colOrigin')}</th>
                      <th className="rr-num">{t('reports.shine.colCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagNominados.visibles.map((r, i) => (
                      <tr key={r.id || i}>
                        <td>{i + 1}</td>
                        <td>{r.suprimido ? <span className="rr-muted">{t('reports.common.masked')}</span> : r.nombre}</td>
                        <td className="rr-rank-tag">{r.suprimido ? '' : (r.ouName ?? '')}</td>
                        <td className="rr-rank-tag">{r.suprimido ? '' : (r.buName ?? '')}</td>
                        <td className="rr-rank-tag">{r.suprimido ? '' : (r.deptName ?? '')}</td>
                        {/* Vacío, no un valor por defecto: el backend manda null cuando no consta. */}
                        <td className="rr-rank-tag">
                          {r.suprimido || !r.fundamentalId ? '' : nombreDeFundamental(r.fundamentalId)}
                        </td>
                        <td className="rr-rank-tag">
                          {r.suprimido ? '' : (r.origenes ?? []).join(' · ')}
                        </td>
                        <td className="rr-num--strong">{r.suprimido ? '—' : r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/*
                  Paginación de PÁGINAS, como la maqueta («Mostrando 1-3 de 3» + página + 10 por pág.).
                  Es de cliente: el `top` de la petición pasa a ser fijo (el «Top 200» de las hojas
                  «Definiciones» del cliente) y esta tabla recorta lo suyo. Antes era solo un selector
                  de tamaño que disparaba una consulta por cada cambio.
                */}
                <Paginacion
                  siempreVisible
                  pagina={pagNominados.pagina}
                  totalPaginas={pagNominados.totalPaginas}
                  total={pagNominados.total}
                  tamano={pagNominados.tamano}
                  onPagina={pagNominados.setPagina}
                  onTamano={pagNominados.cambiarTamano}
                />
              </div>
            )}
            {/*
              * «Ranking Daily Habits» — tabla en pantalla (Tarea 14b: en el Excel ya no es su propia
              * hoja, se pliega como segundo nivel de «Distribución Fundamental»).
              *
              * Se pinta SOLO si hay datos: los agregados anteriores a la dimensión de hábitos no la
              * traen, y una tabla vacía con encabezados haría creer que nadie reconoce hábitos
              * cuando lo que pasa es que aún no se contaban.
              */}
            {rankingHabitos.length > 0 && (
              <div className="rr-table-wrap rr-table-wrap--top-gap">
                <div className="au-card-sub rr-block-title">
                  {t('reports.shine.habitsRanking')}
                </div>

                {/* Filtro PROPIO de esta tabla. Solo se ofrece si hay más de un Fundamental que
                    elegir: con uno solo, el selector no puede cambiar nada. */}
                {fundamentalesConHabitos.length > 1 && (
                  <label className="rr-habits-filter">
                    <span className="rr-sr-only">{t('reports.shine.filterHabitsByFundamental')}</span>
                    <select
                      className="rr-funnel-select"
                      value={habitoFundFiltro}
                      onChange={(e) => setHabitoFundFiltro(e.target.value)}
                    >
                      <option value="">{t('reports.shine.allFundamentals')}</option>
                      {fundamentalesConHabitos.map((f) => (
                        <option key={f.id} value={f.id}>{f.nombre}</option>
                      ))}
                    </select>
                  </label>
                )}

                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>{t('reports.shine.colPos')}</th>
                      <th>{t('reports.shine.colHabit')}</th>
                      <th>{t('reports.daily.colFundamental')}</th>
                      <th className="rr-num">{t('reports.shine.colRecognitions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagHabitos.visibles.map((h, i) => {
                      const padre = padreDelHabito.get(h.id);
                      return (
                        <tr key={h.id}>
                          <td>{i + 1}</td>
                          <td>{nombreDeHabito(h.id)}</td>
                          {/* Vacío si el catálogo no sabe de qué Fundamental cuelga: no se adivina. */}
                          <td className="rr-rank-tag">{padre ? nombreDeFundamental(padre) : ''}</td>
                          <td className="rr-num">{h.count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* La maqueta pinta un pie en ESTA tabla también, no solo en la de nominados. */}
                <Paginacion
                  siempreVisible
                  pagina={pagHabitos.pagina}
                  totalPaginas={pagHabitos.totalPaginas}
                  total={pagHabitos.total}
                  tamano={pagHabitos.tamano}
                  onPagina={pagHabitos.setPagina}
                  onTamano={pagHabitos.cambiarTamano}
                />
              </div>
            )}

            {/*
              * «Distribución por Fundamental» — el mismo bloque del tab Diario, con el mismo
              * componente: es la misma pregunta («¿por qué Fundamental se reconoce?») y tenerla con
              * dos aspectos distintos según la pestaña sería gratuito.
              *
              * Se pinta SOLO si viene: el campo es aditivo y un backend anterior no lo trae. Una
              * gráfica vacía se leería como «no se reconoció por ningún Fundamental», que es una
              * afirmación, y aquí no consta nada.
              *
              * El denominador es `totalNominaciones`, el mismo criterio que el Diario usa con
              * `totalPeriodo`. Los porcentajes pueden sumar más de 100 % y no es un error: un
              * reconocimiento puede llevar varios Fundamentales y cuenta en cada uno.
              */}
            {(report.topFundamentales?.length ?? 0) > 0 && (
              <>
                <h3 className="au-card-title rr-block-heading">
                  {t('reports.daily.fundamentalDistribution')}
                </h3>
                <DistribucionFundamental
                  variante="fina"
                  entradas={report.topFundamentales!}
                  total={report.totalNominaciones}
                  habitos={rankingHabitos}
                />
              </>
            )}

            <div className="rr-actions rr-actions--top-gap">
              {canExportData && (
                <button
                  type="button"
                  className="rr-btn"
                  onClick={handleExportShine}
                  // Fix round 1, hallazgo 1+3 (exportandoShine) — evita doble clic disparando dos
                  // paginaciones completas en paralelo. Hallazgo 5 (catalogoCargando) — sin el
                  // catálogo de Fundamentales cargado, `buildTodosLosReconocimientosSheet` armaría
                  // la matriz de hábitos con CERO columnas en silencio. `catalogoConError` (fix
                  // final) — mismo gate para cuando la query del catálogo FALLÓ (no solo tarda): sin
                  // esto el botón se reactivaba con el catálogo en `undefined` y el export salía con
                  // datos afirmativamente incorrectos en vez de bloquearse.
                  disabled={
                    report.ranking.length === 0 || exportandoShine || catalogoCargando || catalogoConError
                  }
                >
                  <i
                    className={exportandoShine ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoShine ? t('reports.shine.exporting') : t('reports.shine.exportMultiSheet')}
                </button>
              )}

              <div className="rr-seg" role="group" aria-label={t('reports.shine.winnersFormat')}>
                <button
                  type="button"
                  className={format === 'raw' ? 'rr-seg--on' : ''}
                  onClick={() => setFormat('raw')}
                >
                  {t('reports.shine.formatRaw')}
                </button>
                <button
                  type="button"
                  className={format === 'cascaded' ? 'rr-seg--on' : ''}
                  onClick={() => setFormat('cascaded')}
                >
                  {t('reports.shine.formatCascaded')}
                </button>
              </div>
              {canExportData && (
                <button
                  type="button"
                  className="rr-btn rr-btn--ghost"
                  onClick={handleExportWinners}
                  disabled={winners.isFetching || exportandoWinners}
                >
                  <i
                    className={exportandoWinners ? 'fas fa-spinner fa-spin' : 'fas fa-trophy'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoWinners ? t('reports.shine.exporting') : t('reports.shine.exportWinners')}
                </button>
              )}
            </div>

            {/* Fix round 1, hallazgo 1+2+3 — si `handleExportShine` falla (401/500 en cualquiera de
                las hasta 25 idas y vueltas HTTP del export crudo paginado), se AVISA. Mismo patrón
                que `ReportStatus`'s propio banner de error (`.rr-state--error`, `role="alert"`). */}
            {exportShineError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
              </p>
            )}

            {/* Fix final (revisión de rama) — mismo hallazgo, pero en `handleExportWinners`: quedaba
                sin manejo de error propio (Tarea 17 fix round 1 solo cubrió `handleExportShine`). */}
            {exportWinnersError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
              </p>
            )}

          </>
        )}
      </ReportStatus>
      )}
    </div>
  );
}
